import React, { useEffect, useState } from 'react';
import { treatmentPlansAPI, rehabAPI, injuriesAPI, playersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ClipboardList, 
  Play, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Plus,
  Trash2,
  Activity,
  User,
  Heart,
  ExternalLink,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function RehabCheckinPage() {
  const { user } = useAuth();
  const isTrainer = user?.role === 'trainer';
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isTrainer ? 'dashboard' : 'checkin');
  
  // Trainer state
  const [rehabSummary, setRehabSummary] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [players, setPlayers] = useState([]);
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  
  // Create treatment plan modal
  const [createModal, setCreateModal] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState(null);
  const [planForm, setPlanForm] = useState({
    exercises: [{ name: '', sets: '', reps: '', duration_minutes: '', notes: '' }],
    instructions: '',
    estimated_duration_minutes: 30
  });
  
  // Player check-in state
  const [playerRehab, setPlayerRehab] = useState([]);
  const [activeCheckin, setActiveCheckin] = useState(null);
  const [completeModal, setCompleteModal] = useState(false);
  const [completeForm, setCompleteForm] = useState({ notes: '', pain_level: 5 });
  
  // Live feed state
  const [liveFeed, setLiveFeed] = useState([]);

  useEffect(() => {
    loadData();
    
    // Auto-refresh live feed every 30 seconds for trainers
    let interval;
    if (isTrainer) {
      interval = setInterval(() => {
        loadLiveFeed();
      }, 30000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTrainer]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (isTrainer) {
        const [summaryRes, injuriesRes, playersRes, plansRes] = await Promise.all([
          rehabAPI.getSummary(),
          injuriesAPI.getAll(),
          playersAPI.getAll(),
          treatmentPlansAPI.getAll()
        ]);
        setRehabSummary(summaryRes.data);
        setInjuries(injuriesRes.data.filter(i => i.status !== 'cleared'));
        setPlayers(playersRes.data);
        setTreatmentPlans(plansRes.data);
        
        // Load live feed
        await loadLiveFeed();
      } else {
        // Player/coach view - get their rehab for today
        // For now, show all active treatment plans (in a real app, filter by logged-in player)
        const plansRes = await treatmentPlansAPI.getAll();
        setTreatmentPlans(plansRes.data.filter(p => p.active));
        
        // Get today's checkins
        const todayRes = await rehabAPI.getTodayAll();
        setPlayerRehab(todayRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadLiveFeed = async () => {
    try {
      const res = await rehabAPI.getTodayAll();
      setLiveFeed(res.data);
    } catch (error) {
      console.error('Failed to load live feed');
    }
  };

  // Trainer: Create treatment plan
  const handleCreatePlan = async () => {
    if (!selectedInjury) {
      toast.error('Please select an injury');
      return;
    }
    
    const validExercises = planForm.exercises.filter(e => e.name.trim());
    if (validExercises.length === 0) {
      toast.error('Please add at least one exercise');
      return;
    }
    
    try {
      await treatmentPlansAPI.create({
        injury_id: selectedInjury.id,
        player_id: selectedInjury.player_id,
        exercises: validExercises.map(e => ({
          name: e.name,
          sets: e.sets ? parseInt(e.sets) : null,
          reps: e.reps ? parseInt(e.reps) : null,
          duration_minutes: e.duration_minutes ? parseInt(e.duration_minutes) : null,
          notes: e.notes || null
        })),
        instructions: planForm.instructions,
        estimated_duration_minutes: parseInt(planForm.estimated_duration_minutes) || 30
      });
      toast.success('Treatment plan created');
      setCreateModal(false);
      resetPlanForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create plan');
    }
  };

  const resetPlanForm = () => {
    setSelectedInjury(null);
    setPlanForm({
      exercises: [{ name: '', sets: '', reps: '', duration_minutes: '', notes: '' }],
      instructions: '',
      estimated_duration_minutes: 30
    });
  };

  const addExercise = () => {
    setPlanForm(prev => ({
      ...prev,
      exercises: [...prev.exercises, { name: '', sets: '', reps: '', duration_minutes: '', notes: '' }]
    }));
  };

  const removeExercise = (index) => {
    setPlanForm(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
  };

  const updateExercise = (index, field, value) => {
    setPlanForm(prev => ({
      ...prev,
      exercises: prev.exercises.map((e, i) => i === index ? { ...e, [field]: value } : e)
    }));
  };

  // Player: Start rehab
  const handleStartRehab = async (planId) => {
    try {
      const res = await rehabAPI.start(planId);
      setActiveCheckin({ id: res.data.id, planId });
      toast.success('Rehab session started');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start rehab');
    }
  };

  // Player: Complete rehab
  const handleCompleteRehab = async () => {
    if (!activeCheckin) return;
    
    try {
      await rehabAPI.complete(activeCheckin.id, {
        notes: completeForm.notes,
        pain_level: completeForm.pain_level
      });
      toast.success('Rehab completed!');
      setCompleteModal(false);
      setActiveCheckin(null);
      setCompleteForm({ notes: '', pain_level: 5 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete rehab');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#32D74B]/10 text-[#32D74B] text-xs"><CheckCircle className="w-3 h-3" />Completed</span>;
      case 'in_progress':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FFD60A]/10 text-[#FFD60A] text-xs"><Clock className="w-3 h-3" />In Progress</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#71717A]/10 text-[#71717A] text-xs"><AlertCircle className="w-3 h-3" />Not Started</span>;
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="rehab-checkin-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <p className="text-xs text-[#71717A] uppercase tracking-wider">Athletic Training</p>
            <h1 className="font-display text-4xl text-white tracking-tight">REHAB CHECK-IN</h1>
          </div>

          {isTrainer && (
            <Button
              onClick={() => setCreateModal(true)}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              data-testid="create-plan-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Treatment Plan
            </Button>
          )}
          
          {/* Kiosk Link */}
          <a 
            href="/rehab-kiosk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#32D74B] hover:bg-[#32D74B]/90 text-white font-medium rounded-md"
            data-testid="kiosk-link"
          >
            <ExternalLink className="w-4 h-4" />
            Open Rehab Kiosk
          </a>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#121212] border border-[#27272A]">
            {isTrainer && (
              <TabsTrigger 
                value="dashboard" 
                className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                data-testid="dashboard-tab"
              >
                <Activity className="w-4 h-4 mr-2" />
                Trainer Dashboard
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="checkin" 
              className="data-[state=active]:bg-[#32D74B] data-[state=active]:text-white"
              data-testid="checkin-tab"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              {isTrainer ? 'View Plans' : 'My Rehab'}
            </TabsTrigger>
          </TabsList>

          {/* Trainer Dashboard */}
          {isTrainer && (
            <TabsContent value="dashboard" className="mt-6">
              <div className="space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-[#121212] border-[#27272A]">
                    <CardContent className="p-4 text-center">
                      <p className="font-display text-3xl text-white">{rehabSummary.length}</p>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Active Plans</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#121212] border-[#27272A]">
                    <CardContent className="p-4 text-center">
                      <p className="font-display text-3xl text-[#32D74B]">
                        {liveFeed.filter(r => r.status === 'completed').length}
                      </p>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Completed Today</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-[#121212] border-[#27272A]">
                    <CardContent className="p-4 text-center">
                      <p className="font-display text-3xl text-[#FFD60A]">
                        {liveFeed.filter(r => r.status === 'in_progress').length}
                      </p>
                      <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">In Progress</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Live Feed */}
                <Card className="bg-[#121212] border-[#27272A]">
                  <CardHeader className="border-b border-[#27272A]">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-[#32D74B]" />
                        Live Check-ins Today
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadLiveFeed}
                        className="text-[#71717A] hover:text-white"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 max-h-64 overflow-y-auto">
                    {liveFeed.length === 0 ? (
                      <div className="p-8 text-center text-[#71717A]">
                        No check-ins yet today
                      </div>
                    ) : (
                      <div className="divide-y divide-[#27272A]">
                        {liveFeed.map((checkin, index) => (
                          <div key={index} className="p-4 flex items-center justify-between hover:bg-[#1A1A1A]">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${
                                checkin.status === 'completed' ? 'bg-[#32D74B]' : 'bg-[#FFD60A] animate-pulse'
                              }`} />
                              <div>
                                <p className="text-white font-medium">{checkin.player_name}</p>
                                <p className="text-xs text-[#71717A]">
                                  {checkin.status === 'completed' 
                                    ? `Completed at ${new Date(checkin.completed_at).toLocaleTimeString()}`
                                    : `Started at ${new Date(checkin.started_at).toLocaleTimeString()}`
                                  }
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              {checkin.status === 'completed' && checkin.pain_level && (
                                <p className={`text-sm ${
                                  checkin.pain_level <= 3 ? 'text-[#32D74B]' :
                                  checkin.pain_level <= 6 ? 'text-[#FFD60A]' : 'text-[#FF3B30]'
                                }`}>
                                  Pain: {checkin.pain_level}/10
                                </p>
                              )}
                              {getStatusBadge(checkin.status)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Today's Rehab List */}
                <Card className="bg-[#121212] border-[#27272A]">
                  <CardHeader className="border-b border-[#27272A]">
                    <CardTitle className="text-white">All Active Treatment Plans</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {loading ? (
                      <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30] mx-auto"></div>
                      </div>
                    ) : rehabSummary.length === 0 ? (
                      <div className="p-8 text-center text-[#71717A]">
                        No active treatment plans
                      </div>
                    ) : (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#27272A]">
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase">Player</th>
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase">Injury</th>
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase">Status</th>
                            <th className="text-left p-4 text-xs text-[#71717A] uppercase">Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rehabSummary.map((item, index) => (
                            <tr key={index} className="border-b border-[#27272A] hover:bg-[#1A1A1A]">
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  <User className="w-4 h-4 text-[#71717A]" />
                                  <span className="text-white">{item.player_name}</span>
                                </div>
                              </td>
                              <td className="p-4 text-[#A1A1AA]">
                                {item.injury?.injury_type} - {item.injury?.body_part}
                              </td>
                              <td className="p-4">{getStatusBadge(item.status)}</td>
                              <td className="p-4 text-[#71717A] text-sm">
                                {item.today_checkin?.completed_at 
                                  ? new Date(item.today_checkin.completed_at).toLocaleTimeString()
                                  : item.today_checkin?.started_at
                                    ? `Started ${new Date(item.today_checkin.started_at).toLocaleTimeString()}`
                                    : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Check-in / Plans View */}
          <TabsContent value="checkin" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loading ? (
                <div className="col-span-full p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30] mx-auto"></div>
                </div>
              ) : treatmentPlans.filter(p => p.active).length === 0 ? (
                <div className="col-span-full p-8 text-center text-[#71717A]">
                  No active treatment plans
                </div>
              ) : (
                treatmentPlans.filter(p => p.active).map((plan) => {
                  const todayCheckin = playerRehab.find(r => r.treatment_plan_id === plan.id);
                  const status = todayCheckin?.status || 'not_started';
                  
                  return (
                    <Card key={plan.id} className="bg-[#121212] border-[#27272A]" data-testid={`plan-card-${plan.id}`}>
                      <CardHeader className="border-b border-[#27272A] pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white text-lg flex items-center gap-2">
                            <Heart className="w-5 h-5 text-[#FF3B30]" />
                            {plan.player_name}
                          </CardTitle>
                          {getStatusBadge(status)}
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 space-y-4">
                        <div>
                          <p className="text-xs text-[#71717A] uppercase mb-2">Exercises</p>
                          <ul className="space-y-1">
                            {plan.exercises.map((ex, i) => (
                              <li key={i} className="text-sm text-[#A1A1AA]">
                                • {ex.name}
                                {ex.sets && ex.reps && ` (${ex.sets}x${ex.reps})`}
                                {ex.duration_minutes && ` - ${ex.duration_minutes} min`}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {plan.instructions && (
                          <div>
                            <p className="text-xs text-[#71717A] uppercase mb-1">Instructions</p>
                            <p className="text-sm text-[#A1A1AA]">{plan.instructions}</p>
                          </div>
                        )}
                        
                        <div className="text-xs text-[#71717A]">
                          Est. Duration: {plan.estimated_duration_minutes} minutes
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="pt-2 border-t border-[#27272A]">
                          {status === 'not_started' && (
                            <Button
                              onClick={() => handleStartRehab(plan.id)}
                              className="w-full bg-[#32D74B] hover:bg-[#32D74B]/90 text-white"
                              data-testid={`start-rehab-${plan.id}`}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Start Rehab
                            </Button>
                          )}
                          {status === 'in_progress' && (
                            <Button
                              onClick={() => {
                                setActiveCheckin({ id: todayCheckin.id, planId: plan.id });
                                setCompleteModal(true);
                              }}
                              className="w-full bg-[#007AFF] hover:bg-[#007AFF]/90 text-white"
                              data-testid={`complete-rehab-${plan.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Complete Rehab
                            </Button>
                          )}
                          {status === 'completed' && (
                            <div className="text-center text-[#32D74B] text-sm">
                              ✓ Completed Today
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Treatment Plan Modal */}
        <Dialog open={createModal} onOpenChange={(open) => { setCreateModal(open); if (!open) resetPlanForm(); }}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Treatment Plan</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Select Injury */}
              <div>
                <Label className="text-[#A1A1AA]">Select Injury</Label>
                <Select
                  value={selectedInjury?.id || ''}
                  onValueChange={(value) => setSelectedInjury(injuries.find(i => i.id === value))}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white mt-1">
                    <SelectValue placeholder="Select an injury..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    {injuries.map(injury => (
                      <SelectItem key={injury.id} value={injury.id} className="text-white">
                        {injury.player_name} - {injury.injury_type} ({injury.body_part})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Exercises */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[#A1A1AA]">Exercises</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addExercise}
                    className="border-[#27272A] text-white"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                <div className="space-y-3">
                  {planForm.exercises.map((exercise, index) => (
                    <div key={index} className="bg-[#0A0A0A] border border-[#27272A] p-3 space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={exercise.name}
                          onChange={(e) => updateExercise(index, 'name', e.target.value)}
                          placeholder="Exercise name"
                          className="flex-1 bg-[#121212] border-[#27272A] text-white"
                        />
                        {planForm.exercises.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeExercise(index)}
                            className="text-[#FF3B30]"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          type="number"
                          value={exercise.sets}
                          onChange={(e) => updateExercise(index, 'sets', e.target.value)}
                          placeholder="Sets"
                          className="bg-[#121212] border-[#27272A] text-white"
                        />
                        <Input
                          type="number"
                          value={exercise.reps}
                          onChange={(e) => updateExercise(index, 'reps', e.target.value)}
                          placeholder="Reps"
                          className="bg-[#121212] border-[#27272A] text-white"
                        />
                        <Input
                          type="number"
                          value={exercise.duration_minutes}
                          onChange={(e) => updateExercise(index, 'duration_minutes', e.target.value)}
                          placeholder="Duration (min)"
                          className="bg-[#121212] border-[#27272A] text-white"
                        />
                      </div>
                      <Input
                        value={exercise.notes}
                        onChange={(e) => updateExercise(index, 'notes', e.target.value)}
                        placeholder="Notes (optional)"
                        className="bg-[#121212] border-[#27272A] text-white"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructions */}
              <div>
                <Label className="text-[#A1A1AA]">Instructions</Label>
                <Textarea
                  value={planForm.instructions}
                  onChange={(e) => setPlanForm({ ...planForm, instructions: e.target.value })}
                  placeholder="General instructions for the athlete..."
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  rows={3}
                />
              </div>

              {/* Duration */}
              <div>
                <Label className="text-[#A1A1AA]">Estimated Duration (minutes)</Label>
                <Input
                  type="number"
                  value={planForm.estimated_duration_minutes}
                  onChange={(e) => setPlanForm({ ...planForm, estimated_duration_minutes: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModal(false)} className="border-[#27272A] text-white">
                Cancel
              </Button>
              <Button onClick={handleCreatePlan} className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white">
                Create Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Complete Rehab Modal */}
        <Dialog open={completeModal} onOpenChange={setCompleteModal}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white">
            <DialogHeader>
              <DialogTitle>Complete Rehab Session</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#A1A1AA]">Pain Level (1-10)</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setCompleteForm({ ...completeForm, pain_level: level })}
                      className={`w-8 h-8 flex items-center justify-center text-sm font-bold transition-all ${
                        completeForm.pain_level === level
                          ? level <= 3 ? 'bg-[#32D74B] text-white' :
                            level <= 6 ? 'bg-[#FFD60A] text-black' :
                            'bg-[#FF3B30] text-white'
                          : 'bg-[#0A0A0A] border border-[#27272A] text-white hover:border-[#3F3F46]'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Notes (optional)</Label>
                <Textarea
                  value={completeForm.notes}
                  onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })}
                  placeholder="How did it go? Any concerns?"
                  className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompleteModal(false)} className="border-[#27272A] text-white">
                Cancel
              </Button>
              <Button onClick={handleCompleteRehab} className="bg-[#32D74B] hover:bg-[#32D74B]/90 text-white">
                <CheckCircle className="w-4 h-4 mr-2" />
                Complete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
